import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import moment from "moment";
import { Observable } from "rxjs";
import { dateToUtcIsoString } from "src/app/common/datetime_utility";
import { LabelBindingModel } from "src/app/models/api/common";
import { FileDownloadModel } from "src/app/models/api/fileDownload.model";
import { JsonHalCollection } from "src/app/models/api/jsonhal.model";
import { ActionTypes } from "src/app/models/api/playbooks/actions/actionTypes.enum";
import {
    BindLabelToViolation,
    BulkActionSubmitResult,
    ResetViolationsStatusApiModel,
    UpdateViolationsStatusApiModel,
    ViolationAddNote,
    ViolationAudit,
    ViolationModel,
    ViolationNote,
    ViolationsActionBulkApiModel,
    ViolationsDataType,
    ViolationsStatusUpdateResponse,
} from "src/app/models/api/violations.model";
import { ISelectedFilters } from "src/app/models/filters/selectedFilters.interface";
import { SortColumn } from "src/app/models/sortColumn.model";
import { LoggerService } from "../../infra/logger/logger.service";
import { HttpService } from "../http/http.service";

@Injectable({
    providedIn: "root",
})
export class ViolationsHttpService {
    public readonly defaultPageSize: number = 50;
    private readonly _baseUrl: string = "assessment";

    constructor(
        private _httpService: HttpService,
        private _logger: LoggerService,
    ) {}

    public updateViolationStatus(integrationId: string, update: UpdateViolationsStatusApiModel): Observable<ViolationsStatusUpdateResponse> {
        const url = `${this._baseUrl}/integrations/${integrationId}/status_with_audit`;
        return this._httpService.put<ViolationsStatusUpdateResponse, UpdateViolationsStatusApiModel>(url, update);
    }

    public resetViolationStatus(integrationId: string, update: ResetViolationsStatusApiModel): Observable<ViolationsStatusUpdateResponse> {
        const url = `${this._baseUrl}/integrations/${integrationId}/status/reset_with_audit`;
        return this._httpService.put<ViolationsStatusUpdateResponse, ResetViolationsStatusApiModel>(url, update);
    }

    /**
     * @public Apply bulk action
     * @returns an Observable{BulkActionSubmitResult} with the results count from the server
     */
    public applyBulkAction(
        body: ViolationsActionBulkApiModel,
        action: ActionTypes,
        filterOptions?: ISelectedFilters,
        sort?: SortColumn[],
    ): Observable<BulkActionSubmitResult> {
        const url = `${this._baseUrl}/actions_with_audit?type=${action}`;
        const httpParams = this._createViolationsQueryParams(filterOptions, sort, true);

        return this._httpService.put<BulkActionSubmitResult, ViolationsActionBulkApiModel>(url, body, undefined, httpParams);
    }

    /**
     * @public Get vulnerabilities file
     * @returns a file with the vulnerabilities returned from server
     */
    public exportFilteredViolationsCsv(
        violationsDataType: ViolationsDataType,
        filterOptions: ISelectedFilters,
        sort?: SortColumn[],
    ): Observable<FileDownloadModel> {
        const endpointUrl = violationsDataType == ViolationsDataType.violations ? "violations" : "hygiene";
        const url = `${this._baseUrl}/${endpointUrl}/export`;

        const httpParams = this._createViolationsQueryParams(filterOptions, sort);

        return this._httpService.get<FileDownloadModel>(url, undefined, httpParams);
    }

    /**
     * @public Get vulnerabilities
     * @returns an Observable{VulnerabilitiesResponse} with the vulnerabilities returned from server
     */
    public getViolations(filterOptions?: ISelectedFilters, sort?: SortColumn[]): Observable<JsonHalCollection<ViolationModel>> {
        const url = `${this._baseUrl}/violations`;
        const httpParams = this._createViolationsQueryParams(filterOptions, sort, true);

        return this._httpService.get<JsonHalCollection<ViolationModel>>(url, undefined, httpParams);
    }

    /**
     * @public Get hygiene category violations
     * @returns an Observable{VulnerabilitiesResponse} with the vulnerabilities returned from server
     */
    public getHygiene(filterOptions?: ISelectedFilters, sort?: SortColumn[]): Observable<JsonHalCollection<ViolationModel>> {
        const url = `${this._baseUrl}/hygiene`;
        const httpParams = this._createViolationsQueryParams(filterOptions, sort, true);

        return this._httpService.get<JsonHalCollection<ViolationModel>>(url, undefined, httpParams);
    }

    /**
     * @public Get vulnerabilities by url, received from the first call of the api.
     * @returns an Observable{VulnerabilitiesResponse} with the vulnerabilities returned from server
     */
    public getViolationsByContinuationToken(continuationToken: string): Observable<JsonHalCollection<ViolationModel>> {
        const url = `${this._baseUrl}/violations-pages`;
        const httpParams = new HttpParams().append("continuationToken", continuationToken);

        return this._httpService.get<JsonHalCollection<ViolationModel>>(url, undefined, httpParams);
    }

    /**
     * @public Get hyginen violations by url, received from the first call of the api.
     * @returns an Observable{VulnerabilitiesResponse} with the vulnerabilities returned from server
     */
    public getHygieneByContinuationToken(continuationToken: string): Observable<JsonHalCollection<ViolationModel>> {
        const url = `${this._baseUrl}/hygiene-pages`;
        const httpParams = new HttpParams().append("continuationToken", continuationToken);

        return this._httpService.get<JsonHalCollection<ViolationModel>>(url, undefined, httpParams);
    }

    /**
     * @public Get vulnerability by id
     * @returns an Observable{VulnerabilityModel} with the vulnerability returned from server
     */
    public getViolationById(violationId: string, integrationId: string, exempted = false): Observable<ViolationModel> {
        const url = `${this._baseUrl}/integrations/${integrationId}/violations/${violationId}`;
        const httpParams = new HttpParams().append("exempted", exempted);

        return this._httpService.get<ViolationModel>(url, undefined, httpParams);
    }

    /**
     * @public Get notes by violation id
     * @returns an Observable{ViolationNote} with the note added returned from server
     */
    public getNotes(violationId: string, integrationId: string): Observable<ViolationNote[]> {
        const url = `${this._baseUrl}/integrations/${integrationId}/violations/${violationId}/notes`;
        return this._httpService.get<ViolationNote[]>(url);
    }

    /**
     * @public Get audits by violation id
     * @returns an Observable{ViolationAudit} with the audit added returned from server
     */
    public getAudits(violationId: string, integrationId: string): Observable<JsonHalCollection<ViolationAudit>> {
        const url = `${this._baseUrl}/integrations/${integrationId}/violations/${violationId}/auditEvents`;
        return this._httpService.get<JsonHalCollection<ViolationAudit>>(url);
    }

    /**
     * @public add note
     * @returns an Observable{ViolationNote} with the note added returned from server
     */
    public addNote(violationId: string, integrationId: string, note: ViolationAddNote): Observable<ViolationNote> {
        const url = `${this._baseUrl}/integrations/${integrationId}/violations/${violationId}/notes`;
        return this._httpService.post<ViolationAddNote, ViolationNote>(url, note);
    }

    /**
     * @public delete note
     */
    public deleteNote(violationId: string, integrationId: string, noteId: string): Observable<string> {
        const url = `${this._baseUrl}/integrations/${integrationId}/violations/${violationId}/notes/${noteId}`;
        return this._httpService.delete<string>(url);
    }

    /**
     * @public Bind label to violation
     */
    public bindLabelToViolation(violationId: string, integrationId: string, bind: BindLabelToViolation): Observable<LabelBindingModel> {
        const url = `${this._baseUrl}/integrations/${integrationId}/violations/${violationId}/labelBindings`;
        return this._httpService.post<BindLabelToViolation, LabelBindingModel>(url, bind);
    }

    /**
     * @public Unbind label from violation
     */
    public unbindLabelFromViolation(violationId: string, integrationId: string, labelBinding: LabelBindingModel): Observable<string> {
        const url = `${this._baseUrl}/integrations/${integrationId}/violations/${violationId}/labelBindings/${labelBinding.id}`;
        return this._httpService.delete<string>(url);
    }

    private _createViolationsQueryParams(filterOptions?: ISelectedFilters, sortData?: SortColumn[], shouldLimitResultsAmount = false): HttpParams {
        let httpParams = new HttpParams();

        if (shouldLimitResultsAmount) {
            httpParams = httpParams.append("pageSize", this.defaultPageSize);
        }

        if (filterOptions) {
            httpParams = this._appendViolationsFilterQueryParams(httpParams, filterOptions);
        }

        if (sortData) {
            httpParams = this._appendViolationsSortQueryParams(httpParams, sortData);
        }
        return httpParams;
    }

    private _appendViolationsFilterQueryParams(httpParams: HttpParams, filterOptions: ISelectedFilters): HttpParams {
        if (filterOptions.search && filterOptions.search !== "") {
            httpParams = httpParams.append("search", `${filterOptions.search}`);
        }

        if (filterOptions.integration && filterOptions.integration.length > 0) {
            httpParams = httpParams.append("integrationId", filterOptions.integration.join(","));
        }

        if (filterOptions.resourceId && filterOptions.resourceId.length > 0) {
            httpParams = httpParams.append("resourceId", filterOptions.resourceId.join(","));
        }

        if (filterOptions.category && filterOptions.category.length > 0) {
            httpParams = httpParams.append("violationCategory", filterOptions.category.join(","));
        }

        if (filterOptions.platform && filterOptions.platform.length > 0) {
            httpParams = httpParams.append("platform", filterOptions.platform.join(","));
        }

        if (filterOptions.policy && filterOptions.policy.length > 0) {
            httpParams = httpParams.append("policyId", filterOptions.policy.join(","));
        }

        if (filterOptions.resourceStatus && filterOptions.resourceStatus.length > 0) {
            httpParams = httpParams.append("resourceStatus", filterOptions.resourceStatus.join(","));
        }

        if (filterOptions.severity && filterOptions.severity.length > 0) {
            httpParams = httpParams.append("severity", filterOptions.severity.join(","));
        }

        if (filterOptions.type && filterOptions.type.length > 0) {
            httpParams = httpParams.append("resourceType", filterOptions.type.join(","));
        }

        if (filterOptions.violationRule && filterOptions.violationRule.length > 0) {
            httpParams = httpParams.append("violationRuleId", filterOptions.violationRule.join(","));
        }

        if (filterOptions.owasp && filterOptions.owasp.length > 0) {
            httpParams = httpParams.append("owasp", filterOptions.owasp.join(","));
        }

        if (filterOptions.generativeAiOptions && filterOptions.generativeAiOptions.length > 0) {
            httpParams = httpParams.append("generativeAiOptions", filterOptions.generativeAiOptions.join(","));
        }

        if (filterOptions.attack && filterOptions.attack.length > 0) {
            httpParams = httpParams.append("attack", filterOptions.attack.join(","));
        }

        // This is used by the filters in the violations page
        if (filterOptions.violationStatus && filterOptions.violationStatus.length > 0) {
            httpParams = httpParams.append("violationStatus", filterOptions.violationStatus.join(","));
        }

        // This is used by the filters in the violations page
        if (filterOptions.violationStatusReason && filterOptions.violationStatusReason.length > 0) {
            httpParams = httpParams.append("violationStatusReason", filterOptions.violationStatusReason.join(","));
        }

        // this is used on the filters by the hygiene page.
        if (filterOptions.status && filterOptions.status.length > 0) {
            httpParams = httpParams.append("violationStatus", filterOptions.status.join(","));
        }

        if (filterOptions.fromDate) {
            httpParams = httpParams.append("firstSeenSince", dateToUtcIsoString(moment(filterOptions.fromDate).startOf("day").toDate()));
        }

        if (filterOptions.toDate) {
            httpParams = httpParams.append("firstSeenUntil", dateToUtcIsoString(moment(filterOptions.toDate).endOf("day").toDate()));
        }

        if (filterOptions.environment && filterOptions.environment.length > 0) {
            httpParams = httpParams.append("environmentId", `${filterOptions.environment}`);
        }

        if (filterOptions.createdBy && filterOptions.createdBy.length > 0) {
            httpParams = httpParams.append("createdById", `${filterOptions.createdBy}`);
        }

        if (filterOptions.ownedBy && filterOptions.ownedBy.length > 0) {
            httpParams = httpParams.append("ownedById", `${filterOptions.ownedBy}`);
        }

        if (filterOptions?.department?.length) {
            httpParams = httpParams.append("createdByDepartment", `${filterOptions.department}`);
        }

        if (filterOptions?.label?.length) {
            httpParams = httpParams.append("labelId", `${filterOptions.label}`);
        }

        if (filterOptions?.ruleTag?.length) {
            httpParams = httpParams.append("ruleTag", `${filterOptions.ruleTag}`);
        }

        return httpParams;
    }

    private _appendViolationsSortQueryParams(httpParams: HttpParams, sortData: SortColumn[]): HttpParams {
        const sortArray: string[] = [];

        sortData.forEach((sort) => {
            const sortFieldString: string | undefined = sort.column.sortKey;
            const ascOrDescString = sort.isDesc ? "desc" : "asc";
            if (sortFieldString) {
                sortArray.push(`${sortFieldString}(${ascOrDescString})`);
            } else {
                this._logger.error(`Sort violation column ${sort.column.propertyKey} was not implemented`);
            }
        });
        return httpParams.append("sortBy", `[${sortArray}]`);
    }
}
